import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, OnDestroy, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { environment } from 'src/environments/environment';
import { SubSink } from 'subsink';

import {
  BaseScriptFragment,
  CreateScriptGQL,
  DeleteScriptsGQL,
  UpdateScriptGQL,
} from '../../../generated/graphql.generated';
import { DetailsHelperService } from '../../services/details-helper.service';
import { FreyaNotificationsService } from '../../services/freya-notifications.service';
import { ObjectUpdate } from '../../utilities/details-helper.util';

export type MutateScriptType = 'create' | 'update';

/** Sample scripts available as autocomplete options. */
export interface SampleScript {
  label: string;
  url: string;
  description: string;
}

const PROD_SCRIPTS_BASE = 'https://functions.moveright.app/scripts';

function scriptUrl(filename: string): string {
  return environment.production
    ? `${PROD_SCRIPTS_BASE}/${filename}`
    : `${window.location.origin}/functions/scripts/${filename}`;
}

export const SAMPLE_SCRIPTS: SampleScript[] = [
  {
    label: 'Lead Nurture',
    url: scriptUrl('lead-nurture.tsx'),
    description: 'A gentle 5-day follow-up sequence with themed emails and Ask Bee calls for warming up leads',
  },
  {
    label: 'Web Lead Followup',
    url: scriptUrl('web-lead-followup.tsx'),
    description: '5-day automated lead sequence with Ask Bee AI autodial, SMS, and TSX email for online leads',
  },
  {
    label: 'Estimate Followup',
    url: scriptUrl('estimate-followup.tsx'),
    description: '5-day follow-up after an estimate is sent, with Ask Bee AI calls and personalized emails',
  },
  {
    label: 'Valuation Coverage',
    url: scriptUrl('valuation-coverage.tsx'),
    description: 'Sends valuation coverage options email on booking, handles customer click to add charges',
  },
  {
    label: 'Move Day Reminders',
    url: scriptUrl('move-day-reminder.tsx'),
    description: 'SMS reminders 24h and 1h before the Move event with crew arrival window and pickup address',
  },
  {
    label: 'Post-Move Review',
    url: scriptUrl('post-move-review.tsx'),
    description: 'Asks the customer for a Google review 2 days after the move; suppresses if damage/dispute tags',
  },
  {
    label: '7-Day Pre-Move Review',
    url: scriptUrl('pre-move-review.tsx'),
    description: 'Runs 7 days before the move: checks signed estimate, $99 deposit, billed vs calculated, billed vs booked — flags Ask Bee',
  },
  {
    label: 'Deposit Follow-Up',
    url: scriptUrl('deposit-followup.tsx'),
    description: 'Daily deposit reminders for 3 days after booking, then escalates to manager via Ask Bee',
  },
];

/** Regex patterns for extracting metadata from script comment headers. */
const NAME_PATTERN = /@name\s+(.+)/;
const DESC_PATTERN = /@description\s+([\s\S]+?)(?=\n\s*\*\s*@|\n\s*\*\/)/;

@Component({
  selector: 'app-mutate-script',
  templateUrl: './mutate-script.component.html',
  styleUrls: ['./mutate-script.component.scss'],
})
export class MutateScriptComponent implements OnDestroy {

  @Output() saved = new EventEmitter<BaseScriptFragment>();
  @Output() deleted = new EventEmitter<string>();

  script?: BaseScriptFragment;
  mutateType: MutateScriptType = 'create';
  dialogVisible = false;
  loading = false;
  fetchingMeta = false;

  /** Toggle between URL-backed and inline code editing */
  useUrl = false;

  /** Placeholder text for the inline code editor (kept in TS to avoid Angular ICU parsing issues with { }). */
  readonly codePlaceholder = [
    `hook('job-created', (ctx) => {`,
    `  if (ctx.data.job?.stage !== 'lead') return;`,
    `  addTag(ctx, 'New Lead');`,
    ``,
    `  addCommentToJob({`,
    `    mode: 'Ask Bee',`,
    `    text: 'New online lead — load the lead-follow-up procedure.',`,
    `  });`,
    ``,
    `  schedule('followup-sms', '3 min', {}, ctx);`,
    `});`,
    ``,
    `hook('followup-sms', (ctx) => {`,
    `  if (ctx.data.job?.status === 'closed') return;`,
    `  sendSMS({ body: \`Hi \${ctx.data.users?.customer?.givenName}!\` });`,
    `});`,
  ].join('\n');

  /** Filtered list for autocomplete */
  filteredSampleScripts: SampleScript[] = [...SAMPLE_SCRIPTS];

  form = new FormGroup({
    name: new FormControl<string>('', [Validators.required, Validators.maxLength(100)]),
    description: new FormControl<string>(''),
    code: new FormControl<string>(''),
    scriptUrl: new FormControl<string>(''),
    enabled: new FormControl<boolean>(false),
  });

  private subs = new SubSink();

  constructor(
    private createScriptGQL: CreateScriptGQL,
    private updateScriptGQL: UpdateScriptGQL,
    private deleteScriptsGQL: DeleteScriptsGQL,
    private localNotify: FreyaNotificationsService,
    private dhs: DetailsHelperService,
    private http: HttpClient,
  ) {}

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  openDialog(script?: BaseScriptFragment, type: MutateScriptType = 'create') {
    this.script = script;
    this.mutateType = type;
    this.dialogVisible = true;

    if (script) {
      this.useUrl = !!script.scriptUrl && !script.code;
      this.form.setValue({
        name: script.name,
        description: script.description ?? '',
        code: script.code ?? '',
        scriptUrl: script.scriptUrl ?? '',
        enabled: script.enabled,
      });
    } else {
      this.useUrl = false;
      this.form.reset({ name: '', description: '', code: '', scriptUrl: '', enabled: true });
    }
  }

  closeDialog() {
    this.dialogVisible = false;
    this.loading = false;
    this.fetchingMeta = false;
  }

  get isCreate() { return this.mutateType === 'create'; }

  get title() {
    return this.isCreate ? 'Create Script' : `Edit Script: ${this.script?.name ?? ''}`;
  }

  // ─── Autocomplete ───────────────────────────────────────────────────────────

  filterSampleScripts(event: { query: string }) {
    const q = (event.query ?? '').toLowerCase();
    this.filteredSampleScripts = !q
      ? [...SAMPLE_SCRIPTS]
      : SAMPLE_SCRIPTS.filter((s) =>
          s.label.toLowerCase().includes(q) ||
          s.description.toLowerCase().includes(q)
        );
  }

  /** Called when user selects from the autocomplete dropdown. */
  onScriptSelected(event: SampleScript | { value: SampleScript }) {
    const sample: SampleScript = (event as any).value ?? event as SampleScript;
    if (!sample?.url) { return; }

    // If URL mode, set URL; if code mode, fetch and set code
    if (this.useUrl) {
      this.form.patchValue({ scriptUrl: sample.url });
    } else {
      // Fetch the script source and put it in the code field
      this.fetchingMeta = true;
      this.http.get(sample.url, { responseType: 'text' }).subscribe({
        next: (src) => {
          this.fetchingMeta = false;
          this.form.patchValue({ code: src });
        },
        error: () => {
          this.fetchingMeta = false;
          this.localNotify.error('Failed to fetch script source');
        },
      });
    }

    // Auto-fill name + description only if they are currently empty
    if (!this.form.value.name?.trim()) {
      this.form.patchValue({ name: sample.label });
    }
    if (!this.form.value.description?.trim()) {
      this.form.patchValue({ description: sample.description });
    }
  }

  /** Called on blur of the URL input — tries to extract @name/@description from source. */
  onUrlBlur() {
    const url = this.form.value.scriptUrl?.trim();
    if (!url || !url.startsWith('http')) { return; }
    this.tryAutoFillFromUrl(url);
  }

  private tryAutoFillFromUrl(url: string) {
    this.fetchingMeta = true;

    // First: fill from filename as fallback
    const filename = this.filenameFromUrl(url);
    if (filename && !this.form.value.name?.trim()) {
      this.form.patchValue({ name: filename });
    }

    // Then: try to fetch first 50 lines of the script and extract @name + @description
    this.subs.sink = this.http.get(url, { responseType: 'text' })
      .subscribe({
        next: (src) => {
          this.fetchingMeta = false;
          const nameMatch = src.match(NAME_PATTERN);
          const descMatch = src.match(DESC_PATTERN);

          if (nameMatch?.[1]?.trim() && !this.form.value.name?.trim()) {
            this.form.patchValue({ name: nameMatch[1].trim() });
          }
          if (descMatch?.[1] && !this.form.value.description?.trim()) {
            // Clean up multiline description (strip * prefix from each line)
            const desc = descMatch[1]
              .split('\n')
              .map((l) => l.replace(/^\s*\*\s?/, '').trim())
              .filter(Boolean)
              .join(' ');
            this.form.patchValue({ description: desc });
          }
        },
        error: () => {
          this.fetchingMeta = false;
          // Fetch failed — filename fallback already applied above
        },
      });
  }

  private filenameFromUrl(url: string): string {
    try {
      const pathname = new URL(url).pathname;
      const filename = pathname.split('/').pop() ?? '';
      // Remove extension, convert kebab-case to Title Case
      return filename
        .replace(/\.[^.]+$/, '')
        .replace(/-/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
    } catch {
      return '';
    }
  }

  // ─── Save / Delete ──────────────────────────────────────────────────────────

  save() {
    if (this.form.invalid) { return; }
    this.loading = true;

    const { name, description, code, scriptUrl, enabled } = this.form.value;

    if (this.isCreate) {
      this.subs.sink = this.createScriptGQL.mutate({
        name: name!,
        description: description || undefined,
        code: this.useUrl ? undefined : (code || undefined),
        scriptUrl: this.useUrl ? (scriptUrl || undefined) : undefined,
        enabled: enabled ?? false,
      }).subscribe({
        next: (res) => {
          const created = res.data?.createScript;
          this.localNotify.success(`Script "${created?.name}" created.`);
          this.dhs.pushUpdate({ id: created?.id ?? '', type: 'Scripts', action: 'create' } as ObjectUpdate);
          this.saved.emit(created);
          this.closeDialog();
        },
        error: (err) => {
          this.localNotify.apolloError(`Error creating script`, err);
          this.loading = false;
        },
      });
    } else {
      this.subs.sink = this.updateScriptGQL.mutate({
        id: this.script!.id,
        name: name!,
        description: description || null,
        code: this.useUrl ? null : (code || undefined),
        scriptUrl: this.useUrl ? (scriptUrl || undefined) : null,
        enabled: enabled ?? false,
      }).subscribe({
        next: (res) => {
          const updated = res.data?.updateScript;
          this.localNotify.success(`Script "${updated?.name}" updated.`);
          this.dhs.pushUpdate({ id: updated?.id ?? '', type: 'Scripts', action: 'update' } as ObjectUpdate);
          this.saved.emit(updated);
          this.closeDialog();
        },
        error: (err) => {
          this.localNotify.apolloError(`Error updating script`, err);
          this.loading = false;
        },
      });
    }
  }

  confirmDelete() {
    if (!this.script) { return; }
    this.loading = true;
    this.subs.sink = this.deleteScriptsGQL.mutate({ scriptIds: [this.script.id] }).subscribe({
      next: () => {
        this.localNotify.success(`Script "${this.script!.name}" deleted.`);
        this.dhs.pushUpdate({ id: this.script!.id, type: 'Scripts', action: 'delete' } as ObjectUpdate);
        this.deleted.emit(this.script!.id);
        this.closeDialog();
      },
      error: (err) => {
        this.localNotify.apolloError(`Error deleting script`, err);
        this.loading = false;
      },
    });
  }
}
